import React, { useMemo, useState } from 'react';
import styles from './creditPurchaseHistoryTab.module.css';
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import Select from "react-select";
import CalendarDropdown from '../../../dashboards/CalendarDropdown/CalendarDropdown';
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import {
  FeatureCreditPurchaseStatus,
  useListFeatureCreditPurchasesQuery,
} from "../../../../services/api/featureCreditPackagesApi";
import { purchaseAmountLabel } from "../../../../utils/featureCreditPackages";
import Loader from "../../../loader/loader";

const STATUSES: FeatureCreditPurchaseStatus[] = ["Pending", "Completed", "Failed", "Refunded"];

const statusLabel = (status: FeatureCreditPurchaseStatus): string => {
  switch (status) {
    case "Pending":
      return t("PENDING");
    case "Completed":
      return t("STATUS_COMPLETE");
    case "Failed":
      return t("STATUS_FAILED");
    case "Refunded":
      return t("STATUS_REFUNDED");
    default:
      return status;
  }
};

export const CreditPurchaseHistoryTab: React.FC = () => {
  const admin = useAppSelector(adminSelector);

  const [statusFilter, setStatusFilter] = useState<FeatureCreditPurchaseStatus | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  // The calendar gives the end date at midnight, and the backend filters CreatedAt <= to, so
  // without this the last chosen day's purchases are left out.
  const endOfEndDate = useMemo(() => {
    if (!endDate) return undefined;
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [endDate]);

  const { data: purchases = [], isLoading, isError } = useListFeatureCreditPurchasesQuery(
    {
      companyId: admin.companyId,
      status: statusFilter ?? undefined,
      from: startDate,
      to: endOfEndDate,
    },
    { skip: !admin.companyId }
  );

  // Built on every render (not memoized) so the labels follow a language change.
  const statusOptions = [
    { value: "", label: t("ALL_STATUSES") },
    ...STATUSES.map((status) => ({ value: status, label: statusLabel(status) })),
  ];

  const purchasedForLabel = (targetUserId: number | null, targetUserName: string | null): string => {
    if (targetUserName) return targetUserName;
    if (targetUserId) return `#${targetUserId}`;
    return t("SCOPE_WHOLE_COMPANY");
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ul className={styles.filters}>
        <li>
          <div className={styles.filterContainer}>
            <p className={styles.filterTitle}>{t("COLUMN_STATUS")}</p>
            <div className={styles.selectContainer}>
              <Select
                name="status"
                options={statusOptions}
                value={statusOptions.find((o) => o.value === (statusFilter ?? ""))}
                className="customSelect"
                classNamePrefix="customSelect"
                onChange={(value) =>
                  setStatusFilter((value?.value as FeatureCreditPurchaseStatus) || null)
                }
              />
            </div>
          </div>
        </li>
        <li>
          <div className={styles.filterContainer}>
            <CalendarDropdown
              title={t("DATE_RANGE")}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              className={styles.filterTitle}
            />
          </div>
        </li>
      </ul>

      {isError ? (
        <p className="text">{t("SOMETHING_WENT_WRONG_TRY_AGAIN")}</p>
      ) : purchases.length === 0 ? (
        <p className="text">{t("NO_RESULTS_FOUND")}</p>
      ) : (
        <ul className="list">
          <li className={`listRow listRow_header ${styles.purchaseRow}`}>
            <p className="listCell">{t("PURCHASE_DATE")}</p>
            <p className="listCell">{t("PURCHASE_PACKAGE")}</p>
            <p className="listCell">{t("AMOUNT")}</p>
            <p className="listCell">{t("PURCHASED_FOR")}</p>
            <p className="listCell">{t("PURCHASED_BY")}</p>
            <p className="listCell">{t("COLUMN_STATUS")}</p>
            <p className="listCell">{t("INVOICE_REFERENCE")}</p>
          </li>
          {purchases.map((purchase, index) => (
            <li
              key={purchase.id}
              className={`listRow ${styles.purchaseRow} ${index % 2 === 0 ? "colored" : ""}`}
            >
              <p className="listCell">
                {new Date(purchase.createdAt).toLocaleDateString(i18n.language === "he" ? "he-IL" : "en-US")}
              </p>
              <p className="listCell">{purchase.packageName}</p>
              <p className="listCell">{purchaseAmountLabel(purchase)}</p>
              <p className="listCell">{purchasedForLabel(purchase.targetUserId, purchase.targetUserName)}</p>
              <p className="listCell">{purchase.createdByName}</p>
              <p className="listCell">
                <span className={styles[`status_${purchase.status.toLowerCase()}`]}>
                  {statusLabel(purchase.status)}
                </span>
              </p>
              <p className="listCell">
                {purchase.invoiceUrl ? (
                  <a href={purchase.invoiceUrl} target="_blank" rel="noopener noreferrer" className={styles.invoiceLink}>
                    {purchase.invoiceNumber ?? t("INVOICE_REFERENCE")}
                  </a>
                ) : (
                  purchase.invoiceNumber ?? "-"
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
